import { apiClient } from './apiClient';
import { tokenStorage, TokenPair } from './tokenStorage';
import { User } from '../types';

export interface LoginRequest {
  account: string;
  password: string;
}

interface BackendUserPayload {
  id: number;
  account?: string;
  name?: string;
  email?: string;
  displayName?: string;
  role?: string;
  roleAdmin?: boolean;
  roles?: string[];
  userType?: string;
}

interface BackendLoginResponse {
  accessToken?: string;
  refreshToken?: string | null;
  token?: string;
  user: BackendUserPayload;
}

const mapRoles = (payload: BackendUserPayload): string[] => {
  if (Array.isArray(payload.roles) && payload.roles.length > 0) {
    return payload.roles;
  }
  const normalizedRole = (payload.role ?? '').toLowerCase();
  const derivedRoles = new Set<string>();
  if (payload.roleAdmin || normalizedRole === 'admin') {
    derivedRoles.add('admin');
    derivedRoles.add('qa_lead');
  }
  if (normalizedRole === 'field_admin') {
    derivedRoles.add('qa_lead');
  }
  if (normalizedRole === 'inspector' || normalizedRole === 'reviewer') {
    derivedRoles.add('reviewer');
  }
  if (derivedRoles.size === 0 && normalizedRole) {
    derivedRoles.add(normalizedRole);
  }
  if (derivedRoles.size === 0) {
    derivedRoles.add('reviewer');
  }
  return Array.from(derivedRoles);
};

const mapUser = (payload: BackendUserPayload, fallbackAccount: string): User => {
  const displayName = payload.displayName ?? payload.name ?? payload.account ?? fallbackAccount;
  const email = payload.email ?? payload.account ?? fallbackAccount;
  return {
    id: String(payload.id ?? fallbackAccount),
    email,
    displayName,
    roles: mapRoles(payload),
    userType: payload.userType,
  };
};

export const authService = {
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const { data } = await apiClient.post<BackendLoginResponse>('/api/auth/login', {
      account: credentials.account,
      password: credentials.password,
    });

    const accessToken = data.accessToken ?? data.token;
    if (!accessToken) {
      throw new Error('Login response did not include an access token.');
    }

    const tokens: TokenPair = {
      accessToken,
      refreshToken: data.refreshToken ?? undefined,
    };
    tokenStorage.write(tokens);

    const user = mapUser(data.user, credentials.account);
    return {
      accessToken,
      refreshToken: data.refreshToken ?? undefined,
      user,
    };
  },

  logout(): void {
    tokenStorage.clear();
  },
};

export interface LoginResponse {
  accessToken: string;
  refreshToken?: string | null;
  user: User;
}
